import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { midas } from '@resurs/midas'

const Autocomplete = props => {
  const [pickedValues, setPickedValues] = useState(props.defaultValues)
  const [filteredValues, setFilteredValues] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [index, setIndex] = useState(-1)
  const [menuOpen, _setMenuOpen] = useState(false)

  const ref = useRef()
  const menuOpenRef = useRef(menuOpen)

  const setMenuOpen = value => {
    menuOpenRef.current = value
    _setMenuOpen(value)
  }

  useEffect(() => {
    props.onChange(pickedValues)
  }, [pickedValues])

  useEffect(() => {
    setFilteredValues(
      props.values
        .filter(pickedValue => !pickedValues.includes(pickedValue))
        .filter(
          searchItem =>
            (searchItem.value &&
              searchItem.value
                .toLowerCase()
                .includes(searchTerm.toLowerCase())) ||
            (searchItem.key &&
              searchItem.key
                .toString()
                .toLowerCase()
                .includes(searchTerm.toLowerCase())),
        ),
    )
  }, [props.values, searchTerm])

  const handleOutsideClick = e => {
    if (menuOpenRef.current) {
      e.preventDefault()
      e.stopPropagation()
      if (
        e.target.parentNode &&
        e.target.parentNode.id === 'autocomplete-menu'
      ) {
        return false
      }
      if (ref.current && !ref.current.contains(e.target)) {
        setMenuOpen(false)
        setIndex(-1)
        setSearchTerm('')
      }
    }
    return false
  }

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick)
    props.onChange(pickedValues)

    return () => {
      document.removeEventListener('click', handleOutsideClick)
    }
  }, [])

  const handleSearchTermChange = e => {
    e.preventDefault()

    const newSearchTerm = e.target.value != null ? e.target.value : ''

    setSearchTerm(newSearchTerm)
    props.searchTerm(newSearchTerm)

    if (filteredValues.length > 0) {
      setIndex(0)
    }

    if (newSearchTerm.length > 0) {
      setMenuOpen(true)
    }
  }

  const focusAdjacent = dir => {
    if (!props.values) return

    if (menuOpen) {
      let tmpIndex = index

      if (dir === 'next') {
        tmpIndex += 1
      } else if (dir === 'prev') {
        if (tmpIndex > 0) {
          tmpIndex -= 1
        }
      }
      if (tmpIndex === -1) {
        tmpIndex = 0
      }

      setIndex(tmpIndex)
    }
  }

  const selectFocused = () => {
    if (menuOpen) {
      const newItem = filteredValues[index]
      if (newItem) {
        setPickedValues(oldPickedValues => [...oldPickedValues, newItem])
      }
      setSearchTerm('')
    }
  }

  const slicePickedValues = () => {
    setMenuOpen(false)
    setPickedValues(oldPickedValues => oldPickedValues.slice(0, -1))
  }

  const handleKeyDown = e => {
    switch (e.keyCode) {
      case 46:
      case 8: // backspace
        if (searchTerm === '' || searchTerm == null) {
          e.preventDefault()
          slicePickedValues()
        }
        return
      case 13: // enter
        if (!menuOpen) return
        e.stopPropagation()
        setIndex(0)
        setMenuOpen(false)
        selectFocused()
        break
      case 27: // escape
        if (menuOpen) {
          setMenuOpen(false)
          e.stopPropagation()
        }
        break
      case 40: // up
        focusAdjacent('next')
        break
      case 38: // down
        focusAdjacent('prev')
        break
      default:
        return
    }
    e.preventDefault()
  }

  const handleRemoveItem = (e, item) => {
    e.preventDefault()
    setPickedValues(oldPickedValues =>
      oldPickedValues.filter(oldItem => oldItem !== item),
    )
  }

  const handleMenuOpenToggle = e => {
    e.preventDefault()
    e.stopPropagation()
    setMenuOpen(!menuOpen)
    return false
  }

  const handleSelection = (e, item) => {
    e.preventDefault()
    if (props.multiple) {
      setPickedValues(oldPickedValues => [...oldPickedValues, item])
      setSearchTerm('')
    } else {
      setPickedValues([item])
      setSearchTerm('')
      setMenuOpen(false)
      setIndex(-1)
    }
  }

  const handleClear = e => {
    e.preventDefault()
    e.stopPropagation()

    setPickedValues([])
    setSearchTerm('')
    setMenuOpen(false)
    setIndex(-1)

    props.onClear()

    return false
  }

  return (
    <section ref={ref}>
      <div className={midas.formGroup} data-cy={props.dataCy}>
        <label className={midas.formLabel} htmlFor={props.id}>
          {props.label}
        </label>
        <div className={midas.formAutocomplete}>
          <div
            className={[midas.formAutocompleteInput, midas.formInput]
              .filter(val => val)
              .join(' ')}
          >
            {props.multiple
              ? pickedValues.map(item => (
                  <span key={item.key} className={midas.chip}>
                    {item.value}
                    <button
                      type="button"
                      className={[midas.button, midas.buttonClear]
                        .filter(val => val)
                        .join(' ')}
                      aria-label="Ta bort"
                      onClick={e => handleRemoveItem(e, item)}
                    />
                  </span>
                ))
              : pickedValues.length >= 1 &&
                pickedValues[0] && (
                  <span
                    style={{
                      padding: '1rem 0 0 .4rem',
                    }}
                  >
                    {pickedValues[0].value}
                  </span>
                )}

            <div className={midas.formInputWrap}>
              {pickedValues <= 0 && (
                <input
                  id={props.id}
                  className={midas.formInput}
                  type="text"
                  value={searchTerm}
                  placeholder={props.placeholder}
                  onChange={e => handleSearchTermChange(e)}
                  onKeyDown={e => handleKeyDown(e)}
                />
              )}
              <div className={midas.forceRight}>
                {props.loading && <i className={midas.loading} />}
                {!props.multiple && pickedValues.length > 0 && (
                  // eslint-disable-next-line jsx-a11y/control-has-associated-label
                  <button
                    type="button"
                    id="remove-item"
                    className={[midas.button, midas.buttonClear]
                      .filter(val => val)
                      .join(' ')}
                    onClick={e => handleClear(e)}
                  />
                )}
                <button
                  type="button"
                  style={{
                    border: 0,
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                  onClick={e => handleMenuOpenToggle(e)}
                >
                  <i className={midas.iconCaret} />
                </button>
              </div>
            </div>
          </div>
          {menuOpen && (
            <ul
              style={{
                maxHeight: '24rem',
                overflowY: 'scroll',
              }}
              className={midas.menu}
            >
              {filteredValues.map((item, _index) => (
                <li
                  key={item.key}
                  className={`${midas.menuItem} ${
                    _index === index ? midas.active : null
                  }`}
                >
                  <div
                    id="autocomplete-menu"
                    className={midas.autocompleteTile}
                  >
                    <div
                      className={midas.autocompleteTileContent}
                      role="menu"
                      tabIndex={-1}
                      onClick={e => handleSelection(e, item)}
                      onKeyDown={() => {}}
                    >
                      {item.value}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  )
}

Autocomplete.propTypes = {
  values: PropTypes.array.isRequired,
  id: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  defaultValues: PropTypes.array,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  searchTerm: PropTypes.func,
  loading: PropTypes.bool,
  label: PropTypes.string,
  multiple: PropTypes.bool,
  dataCy: PropTypes.string,
}

Autocomplete.defaultProps = {
  multiple: false,
  defaultValues: [],
  loading: false,
  label: '',
  placeholder: '',
  searchTerm: () => {},
  onChange: () => {},
  onClear: () => {},
  dataCy: null,
}

export default Autocomplete
